import { CommonModule } from '@angular/common';
import { Component, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AttachmentComponent } from '../../../shared/attachment/attachment.component';
import { MaterialModule } from "../../../shared/material/material.module";
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CertificationProfessionInfoDTO } from '../../../core/generated/model/certificationProfessionInfoDTO';
import { CertificationProviderPJInfoDTO } from '../../../core/generated/model/certificationProviderPJInfoDTO';
import { CertificationControllerService, CustomerServiceControllerService, CertificationProviderPFInfoDTO, CustomerService } from '../../../core/generated';

@Component({
    selector: 'app-certificate-info',
    standalone: true,
    templateUrl: './certificate-info.component.html',
    imports: [AttachmentComponent, CommonModule, MaterialModule]
})
export class CertificateInfoComponent {
    certificationProfessionInfoDTO!: CertificationProfessionInfoDTO;
    certificationProviderPJInfoDTO!: CertificationProviderPJInfoDTO;
    certificationProviderPFInfoDTO!: CertificationProviderPFInfoDTO;
    profile: string | null = null;
    id: string | null = null;
    csId: string | null = null; // customer service id from route

    constructor(
        public certificationService: CertificationControllerService,
        private route: ActivatedRoute,
        private cdr: ChangeDetectorRef,
        private customerServiceService: CustomerServiceControllerService,
        private _snackBar: MatSnackBar,
        private router: Router
    ) {
        this.profile = this.route.snapshot.paramMap.get('profile');
        this.id = this.route.snapshot.paramMap.get('id');
        this.csId = this.route.snapshot.paramMap.get('csId');
        this.loadCertificateInfo();
    }

    loadCertificateInfo() {
        // Validate required parameters
        if (!this.id) {
            console.error('ID parameter is missing');
            this._snackBar.open('ID do certificado não informado', 'Fechar', { duration: 5000 });
            return;
        }

        if (!this.profile) {
            console.error('Profile parameter is missing');
            this._snackBar.open('Tipo de perfil não informado', 'Fechar', { duration: 5000 });
            return;
        }

        const id = Number(this.id);
        
        // Validate that ID is a valid number
        if (isNaN(id) || id <= 0) {
            console.error('Invalid ID parameter:', this.id);
            this._snackBar.open('ID do certificado inválido', 'Fechar', { duration: 5000 });
            return;
        }

        // Handle different profile types
        switch (this.profile) {
            case 'profession':
                this.loadProfessionCertificationInfo(id);
                break;
            case 'providerPJ':
                this.loadProviderPJCertificationInfo(id);
                break;
            case 'providerPF':
                this.loadProviderPFCertificationInfo(id);
                break;
            default:
                console.error('Unknown profile type:', this.profile);
                this._snackBar.open('Tipo de perfil desconhecido', 'Fechar', { duration: 5000 });
        }
    }

    private loadProfessionCertificationInfo(id: number) {
        this.certificationService.getCertificationProfessionInfo(id)
            .subscribe({
                next: (data: CertificationProfessionInfoDTO) => {
                    if (data?.attachments) {
                        // Sort attachments by ID for consistent display
                        data.attachments = [...data.attachments].sort((a, b) => (a.id || 0) - (b.id || 0));
                    }
                    this.certificationProfessionInfoDTO = data;
                    this.cdr.markForCheck();
                }
            });
    }

    private loadProviderPJCertificationInfo(id: number) {
        this.certificationService.getCertificationproviderPJInfo(id)
            .subscribe({
                next: (data: CertificationProviderPJInfoDTO) => {
                    if (data?.attachments) {
                        // Sort attachments by ID for consistent display
                        data.attachments = [...data.attachments].sort((a, b) => (a.id || 0) - (b.id || 0));
                    }
                    this.certificationProviderPJInfoDTO = data;
                    this.cdr.markForCheck();
                }
            });
    }

    private loadProviderPFCertificationInfo(id: number) {
        this.certificationService.getCertificationproviderPFInfo(id)
            .subscribe({
                next: (data: CertificationProviderPFInfoDTO) => {
                    if (data?.attachments) {
                        // Sort attachments by ID for consistent display
                        data.attachments = [...data.attachments].sort((a, b) => (a.id || 0) - (b.id || 0));
                    }
                    this.certificationProviderPFInfoDTO = data;
                    this.cdr.markForCheck();
                }
            });
    }

    toggleCertification(id: number | undefined, type: string, value: boolean) {
        if (!id) {
            console.error('ID inválido para certificação');
            return;
        }

        let request$;

        switch (type) {
            case 'user':
                request$ = this.certificationService.certifyUserRef(id, value);
                break;
            case 'profileProviderPF':
                request$ = this.certificationService.certifyProfileProviderPF(id, value);
                break;
            case 'profileProviderPJ':
                request$ = this.certificationService.certifyProfileProviderPJ(id, value);
                break;
            case 'council':
                request$ = this.certificationService.certifyCouncil(id, value);
                break;
            case 'profession':
                request$ = this.certificationService.certifyCouncilProfession(id, value);
                break;
            case 'document':
                request$ = this.certificationService.certifyAttachment(id, value);
                break;
            default:
                console.error('Tipo de certificação desconhecido:', type);
                return;
        }

        request$.subscribe({
            next: () => this.loadCertificateInfo()
        });
    }

    confirmClose() {
        if (!this.csId) {
            console.error('csId não informado');
            return;
        }
        const ok = confirm('Deseja realmente encerrar o atendimento relacionado a este certificado?');
        if (ok) {
            this.closeCustomerService();
        }
    }

    async closeCustomerService() {
        if (!this.csId) {
            console.error('csId não informado');
            return;
        }

        const cs = await firstValueFrom(this.customerServiceService.findById13(Number(this.csId)));
        if (!cs) {
            this._snackBar.open('Atendimento não encontrado', 'Fechar', { duration: 5000 });
            return;
        }

        cs.registerDateClose = new Date().toISOString();
        cs.status = 'CUSTOMERSERVICE_CLOSED' as any;

        await firstValueFrom(this.customerServiceService.update14(Number(this.csId), cs));

        this._snackBar.open('Atendimento finalizado com sucesso!', 'Fechar', {
            duration: 5000,
        });
        // volta para a lista de admin
        this.router.navigate(['admin']);
    }

}
